import React from 'react';
import ReactMarkdown from 'react-markdown';
import { styled } from 'linaria/react';
import { css, cx } from 'linaria';
import {
  render,
  NODE_HEADING,
  NODE_IMAGE,
  NODE_PARAGRAPH
} from 'storyblok-rich-text-react-renderer';
import {
  BaseHeading,
  Heading5,
  Quote,
  BaseText
} from '../../../../ui/Typography';
import Image from '../../components/Image';
import { theme } from '../../../../Theme';
import MaxWidth from '../../../../Layout/MaxWidth';
import YotpoGallery from '../../../../ProductPage/YotpoGallery';
import FindifyRecommendationsBlok from '../../bloks/FindifyRecommendations.loadable';

const PageContainer = css`
  h1 {
    ${BaseHeading};
    font-size: 2.25rem;
    text-align: center;
    margin: 3.5rem auto;

    ${theme.below.sm} {
      font-size: 1.5rem;
    }
  }
`;

const CustomWidth = styled(MaxWidth)`
  max-width: 880px;
`;

const contentStyles = css`
  ${BaseText};
  margin-bottom: 10rem;

  ${theme.below.md} {
    margin-bottom: 5rem;
  }

  h2 {
    ${Heading5};
    margin-top: 3.5rem;
    margin-bottom: 0.5rem;
    line-height: 1.5;

    ${theme.below.sm} {
      margin-top: 2rem;
    }
  }

  h2:first-of-type {
    margin-top: 0;
  }

  p + p {
    margin-top: 1.5rem;
  }

  p > a {
    color: ${theme.colors.black};
    text-decoration: underline;
  }
`;

const tableStyles = css`
  width: 100%;
  margin-top: 3.5rem;

  ${theme.below.sm} {
    margin-top: 2rem;
    margin-left: -1rem;
    padding-left: 1rem;
    width: calc(100vw - 1rem);
    position: relative;

    &:after {
      content: '';
      display: block;
      background: linear-gradient(
        90deg,
        rgba(255, 255, 255, 0),
        rgba(255, 255, 255, 1)
      );
      width: 28px;
      position: absolute;
      top: 0;
      bottom: 0;
      right: 0;
    }
  }

  &.col-5 {
    ${theme.above.md} {
      max-width: 1100px;
      width: calc(100% + 220px);
      margin-left: -110px;
    }
  }

  .inner {
    overflow-x: auto;
    width: 100%;
  }

  table {
    width: 100%;

    ${theme.below.sm} {
      border-right: 1rem solid ${theme.colors.white};
    }
  }

  td,
  th {
    min-width: 8.25rem;
    border: 0;
    border-collapse: collapse;
    padding: 0.25rem 0.5rem;
    vertical-align: top;
    font-size: 0.875rem;
    line-height: 1.5;
    color: ${theme.colors.text};
    overflow-wrap: break-word;
    hyphens: auto;
    -ms-hyphens: auto;
    -webkit-hyphens: auto;
    -moz-hyphens: auto;

    ${theme.above.sm} {
      padding: 1rem 0.75rem;
    }

    ${theme.above.md} {
      padding: 1rem 1.25rem;
      font-size: 1rem;
    }
  }

  th {
    text-align: left;
    font-weight: 600;
    color: ${theme.colors.black};
  }

  tr:nth-child(odd) {
    background-color: ${theme.colors.white};
  }

  tr:nth-child(even) {
    background-color: ${theme.colors.offWhite};
  }

  td > p {
    display: inline-block;
    width: 100%;
  }

  td > p + p {
    margin-top: 0.15em;
  }
`;

const ContentPage = content => {
  if (!content) return null;
  const { page_title, content: richTextContent } = content;

  return (
    <article className={PageContainer}>
      <CustomWidth>
        <h1>{page_title}</h1>
        <section className={contentStyles}>
          {render(richTextContent, {
            nodeResolvers: {
              [NODE_HEADING]: (children, props) => (
                <h2 {...props}>{children}</h2>
              ),
              [NODE_IMAGE]: (children, props) => {
                const dimensions = props.src.split('/')[5].split('x');
                const width = dimensions[0];
                const height = dimensions[1];
                return (
                  <Image
                    cover={true}
                    aspect={`${width}:${height}`}
                    width={1328}
                    className="inline-image narrow add-margin"
                    {...props}
                  />
                );
              }
            },
            blokResolvers: {
              image: props => {
                const dimensions = props.image.filename
                  .split('/')[5]
                  .split('x');
                const width = dimensions[0];
                const height = dimensions[1];
                return (
                  <Image
                    cover={true}
                    className="featured-image add-margin"
                    aspect={`${width}:${height}`}
                    width={1328}
                    src={props.image.filename}
                    alt={props.image.alt}
                  />
                );
              },
              video: props => {
                const videoUrl = props.embed_url.url;
                const videoId = videoUrl.includes('youtu.be')
                  ? videoUrl.split('/')[3]
                  : videoUrl.split('/watch?v=')[1];

                return (
                  <div className="narrow">
                    <div className="video-wrapper add-margin">
                      <iframe
                        width="880"
                        height="495"
                        src={`https://www.youtube.com/embed/${videoId}?rel=0&amp;modestbranding=1&amp;showinfo=0`}
                        frameborder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowfullscreen
                      ></iframe>
                    </div>
                  </div>
                );
              },
              table: props => {
                const { table } = props;
                return (
                  <div className={cx(tableStyles, `col-${table.thead.length}`)}>
                    <div className="inner">
                      <table key={table._uid}>
                        <tr>
                          {table.thead.map(head => (
                            <th key={head._uid}>{head.value}</th>
                          ))}
                        </tr>
                        {table.tbody.map(row => (
                          <tr key={row._uid}>
                            {row.body.map(col => (
                              <td key={col._uid}>
                                <ReactMarkdown children={col.value} />
                              </td>
                            ))}
                          </tr>
                        ))}
                      </table>
                    </div>
                  </div>
                );
              },
              yotpo: props => {
                return <YotpoGallery galleryId={props.galleryId} />;
              },
              findify_recommendations: props => (
                <FindifyRecommendationsBlok recIdString={props.recIdString} />
              )
            },
            defaultBlokResolver: (name, props) => (
              <p>
                Missing resolver for <b>"{name}"</b>
              </p>
            )
          })}
        </section>
      </CustomWidth>
    </article>
  );
};

export default ContentPage;
